import React, { useState, useRef, useEffect } from 'react';
import ConfirmModal from '../../../components/ConfirmModal.jsx';
import Toast from '../../../components/Toast.jsx';
import UserList from './UserList';
import './Admin.scss';
import { useQuery } from '@apollo/client/react';
import { gql } from '@apollo/client';
import { publicationTags } from '../../../config/publicationTags.js';

// ── GraphQL ────────────────────────────────────────────────────────────────────

const DATA_ACCESS_OPTIONS_QUERY = gql`
  query DataAccessOptions {
    dataAccessOptions {
      products {
        product
        activities {
          activity
          regions
        }
      }
    }
  }
`;

const OUTLOOK_OPTIONS_QUERY = gql`
  query OutlookOptions {
    outlookOptions {
      globalOutlooks {
        key
        label
      }
      regionalWatches {
        key
        label
      }
    }
  }
`;

// ── Helpers ────────────────────────────────────────────────────────────────────

function getProductTagClass(product) {
  const p = product?.toUpperCase() || '';
  if (p.includes('CRUDE') || p.includes('CONDENSATE')) return 'crude';
  if (p.includes('ETHANOL') || p.includes('BIODIESEL') || p.includes('RENEWABLE') || p.includes('RIN')) return 'renewables';
  if (p.includes('NAPHTHA') || p.includes('PROPANE') || p.includes('BUTANE') || p.includes('ETHANE') || p.includes('LPG')) return 'petrochemical';
  return 'fuels';
}

// ── Helpers ────────────────────────────────────────────────────────────────────

function toSet(val) {
  if (!val) return new Set();
  if (val instanceof Set) return val;
  return new Set(Array.isArray(val) ? val : []);
}

function serialiseAccess(access) {
  return Object.entries(access).map(([product, val]) => ({
    product,
    activities: [...val.activities].map(a => ({ activity: a })),
    regions:    [...val.regions].map(r => ({ region: r })),
  }));
}

// ── CheckGroup — reusable checkbox list with select-all ────────────────────────

function CheckGroup({ items, selected, onChange, columns = 1 }) {
  const allChecked  = items.every(i => selected.has(i.value));
  const someChecked = items.some(i => selected.has(i.value));

  const masterRef = useRef(null);
  React.useEffect(() => {
    if (masterRef.current) {
      masterRef.current.indeterminate = someChecked && !allChecked;
    }
  }, [someChecked, allChecked]);

  const toggleAll = (e) => {
    const next = new Set(e.target.checked ? items.map(i => i.value) : []);
    onChange(next);
  };

  const toggleOne = (value) => {
    const next = new Set(selected);
    next.has(value) ? next.delete(value) : next.add(value);
    onChange(next);
  };

  return (
    <>
      <label className="select-all-row">
        <input type="checkbox" ref={masterRef} checked={allChecked} onChange={toggleAll} />
        Select all
      </label>
      <div className="check-list" style={columns > 1 ? { display: 'grid', gridTemplateColumns: `repeat(${columns}, 1fr)` } : undefined}>
        {items.map(i => (
          <label key={i.value} className="check-item">
            <input
              type="checkbox"
              checked={selected.has(i.value)}
              onChange={() => toggleOne(i.value)}
            />
            {i.label}
          </label>
        ))}
      </div>
    </>
  );
}

// ── Main component ─────────────────────────────────────────────────────────────

export default function CustomerForm({ customer = {}, onSave, onCancel, onDelete }) {
  const [activeTab, setActiveTab] = useState('details');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState(null);

  // Customer details state
  const [customerState, setCustomerState] = useState({
    name:      customer.name      || '',
    industry:  customer.industry  || '',
    startDate: customer.startDate || customer.subscriptionStartDate || '',
    endDate:   customer.endDate   || customer.subscriptionEndDate   || '',
    notes:     customer.notes     || '',
    users:     customer.users     || [],
    ...customer,
  });

  // Outlook options from API
  const { data: outlookOptionsData } = useQuery(OUTLOOK_OPTIONS_QUERY);
  const globalOutlookItems   = (outlookOptionsData?.outlookOptions?.globalOutlooks  ?? []).map(o => ({ value: o.key, label: o.label }));
  const regionalWatchItems   = (outlookOptionsData?.outlookOptions?.regionalWatches ?? []).map(o => ({ value: o.key, label: o.label }));

  // Data access options from API
  const { data: optionsData } = useQuery(DATA_ACCESS_OPTIONS_QUERY);
  const allProducts = optionsData?.dataAccessOptions?.products ?? [];

  // Data Access state: { [product]: { activities: Set, regions: Set } }
  const [access, setAccess] = useState(() => {
    const initial = {};
    if (Array.isArray(customer.dataAccess)) {
      for (const item of customer.dataAccess) {
        initial[item.product] = {
          activities: toSet(item.activities?.map(a => a.activity)),
          regions:    toSet(item.regions?.map(r => r.region)),
        };
      }
    }
    return initial;
  });

  // Outlooks state — API stores a flat [{ key, label }] array; split by key prefix
  const [outlooks, setOutlooks] = useState(() => {
    const global   = new Set();
    const regional = new Set();
    if (Array.isArray(customer.outlooks)) {
      for (const o of customer.outlooks) {
        if (o.key.startsWith('global-'))   global.add(o.key);
        else if (o.key.startsWith('regional-')) regional.add(o.key);
      }
    }
    return { global, regional };
  });

  // Publication access state
  const [pubAccess, setPubAccess] = useState(() => new Set(customer.publicationAccess || []));

  // Publication tag items for CheckGroup
  const pubTagItems = publicationTags.map(({ id, label }) => ({ value: id, label }));

  // Data Access view mode
  const [viewMode, setViewMode] = useState('mine'); // 'mine' | 'all'
  const [showGrantAllModal, setShowGrantAllModal] = useState(false);
  const [animatingOut, setAnimatingOut] = useState(new Set());

  const handleChange = (field) => (e) => {
    const value = e.target.type === 'number' ? Number(e.target.value) : e.target.value;
    setCustomerState(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!customerState.name) return;
    setSaving(true);
    setSaveSuccess(false);
    setSaveError(null);
    try {
      const saved = await onSave({
        ...customerState,
        dataAccess: serialiseAccess(access),
        outlooks:   [...outlooks.global, ...outlooks.regional].map(key => {
          const item = [...globalOutlookItems, ...regionalWatchItems].find(o => o.value === key);
          return { key, label: item?.label ?? key };
        }),
        publicationAccess: Array.from(pubAccess),
      });
      if (saved) {
        setCustomerState(prev => ({ ...prev, ...saved }));
        setSaveSuccess(true);
      }
    } catch (err) {
      const msg = err?.graphQLErrors?.[0]?.message
        || err?.message?.replace(/^GraphQL error:\s*/i, '')
        || 'Failed to save customer';
      setSaveError(msg);
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    if (!saveSuccess) return;
    const t = setTimeout(() => setSaveSuccess(false), 3000);
    return () => clearTimeout(t);
  }, [saveSuccess]);

  useEffect(() => {
    if (!saveError) return;
    const t = setTimeout(() => setSaveError(null), 5000);
    return () => clearTimeout(t);
  }, [saveError]);

  // ── Data Access helpers ──────────────────────────────────────────────────────

  const grantProduct = (key) => {
    if (access[key]) return;
    setAccess(prev => ({
      ...prev,
      [key]: { activities: new Set(), regions: new Set() },
    }));
  };

  const grantAll = () => {
    const next = {};
    for (const p of allProducts) {
      next[p.product] = {
        activities: new Set(p.activities.map(a => a.activity)),
        regions:    new Set(p.activities.flatMap(a => a.regions)),
      };
    }
    setAccess(next);
    setShowGrantAllModal(false);
  };

  const removeProduct = (key) => {
    if (viewMode === 'mine') {
      setAnimatingOut(prev => new Set([...prev, key]));
      setTimeout(() => {
        setAccess(prev => { const next = { ...prev }; delete next[key]; return next; });
        setAnimatingOut(prev => { const next = new Set(prev); next.delete(key); return next; });
      }, 300);
    } else {
      setAccess(prev => { const next = { ...prev }; delete next[key]; return next; });
    }
  };

  const setProductActivities = (key, next) =>
    setAccess(prev => ({ ...prev, [key]: { ...prev[key], activities: next } }));

  const setProductRegions = (key, next) =>
    setAccess(prev => ({ ...prev, [key]: { ...prev[key], regions: next } }));

  const accessKeys = Object.keys(access);

  // ── Outlooks helpers ─────────────────────────────────────────────────────────

  const setOutlookGroup = (group, next) =>
    setOutlooks(prev => ({ ...prev, [group]: next }));

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <div className="admin-card">
      <Toast message="Customer saved successfully." visible={saveSuccess} type="success" />
      <Toast message={saveError} visible={!!saveError} type="error" />
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <h2 style={{ margin: 0 }}>{customerState.id ? 'Edit Customer' : 'Add Customer'}</h2>
        <button
          type="button"
          onClick={onCancel}
          style={{ background: 'none', border: 'none', fontSize: 22, lineHeight: 1, cursor: 'pointer', color: '#666', padding: '0 4px' }}
          aria-label="Close"
        >
          ×
        </button>
      </div>

      {/* Tabs */}
      <div className="admin-tabs">
        {[
          { key: 'details',  label: 'Customer Details' },
          { key: 'access',   label: 'Data Access' },
          { key: 'outlooks', label: 'Outlooks' },
          { key: 'publications', label: 'Publications' },
          { key: 'users',    label: 'Users', disabled: !customerState.id },
        ].map(t => (
          <button
            key={t.key}
            className={`admin-tab${activeTab === t.key ? ' active' : ''}`}
            onClick={() => !t.disabled && setActiveTab(t.key)}
            disabled={t.disabled}
            style={t.disabled ? { opacity: 0.4, cursor: 'not-allowed' } : undefined}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* ── Tab: Customer Details ── */}
      <div className={`admin-panel${activeTab === 'details' ? ' active' : ''}`}>
        <form onSubmit={handleSubmit}>
          <div className="field-group">
            <label className="field-label">
              Name
              <input
                className="admin-input"
                value={customerState.name}
                onChange={handleChange('name')}
                required
              />
            </label>
            <label className="field-label">
              Industry
              <input
                className="admin-input"
                value={customerState.industry}
                onChange={handleChange('industry')}
              />
            </label>
            <label className="field-label">
              Start Date
              <input
                className="admin-input"
                type="date"
                value={customerState.startDate ? customerState.startDate.slice(0, 10) : ''}
                onChange={handleChange('startDate')}
              />
            </label>
            <label className="field-label">
              End Date
              <input
                className="admin-input"
                type="date"
                value={customerState.endDate ? customerState.endDate.slice(0, 10) : ''}
                onChange={handleChange('endDate')}
              />
            </label>
            <label className="field-label">
              Notes
              <textarea
                className="admin-textarea"
                value={customerState.notes}
                onChange={handleChange('notes')}
                placeholder="Any notes about this customer..."
              />
            </label>
          </div>

          {customerState._updatedTs && (
            <p className="last-updated">
              Last updated: {new Date(customerState._updatedTs).toLocaleString()}
            </p>
          )}

            <div className="btn-row">
            <button
              type="submit"
              className="btn btn-primary"
              disabled={!customerState.name || saving}
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
            <button type="button" className="btn btn-secondary" onClick={onCancel}>
              Cancel
            </button>
            {customerState.id && (
              <button
                type="button"
                className="btn btn-danger"
                onClick={() => setShowDeleteModal(true)}
              >
                Delete
              </button>
            )}
          </div>
        </form>

        <ConfirmModal
          open={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          onConfirm={() => { setShowDeleteModal(false); onDelete && onDelete(customerState); }}
          title="Delete Customer"
          message="Are you sure you want to delete this customer? This action cannot be undone."
          confirmLabel="Delete"
          cancelLabel="Cancel"
          confirmClass="button-danger"
          cancelClass="button-secondary"
        />
      </div>

      {/* ── Tab: Data Access ── */}
      <div className={`admin-panel${activeTab === 'access' ? ' active' : ''}`}>
        <div className="access-toolbar">
          <div className="view-toggle">
            <button
              className={`toggle-btn${viewMode === 'mine' ? ' active' : ''}`}
              onClick={() => setViewMode('mine')}
            >
              My Products
            </button>
            <button
              className={`toggle-btn${viewMode === 'all' ? ' active' : ''}`}
              onClick={() => setViewMode('all')}
            >
              All Products
            </button>
          </div>
          <button
            className="btn btn-secondary"
            onClick={() => setShowGrantAllModal(true)}
          >
            ✓ Grant All Permissions
          </button>
        </div>

        <div className="product-list">
          {viewMode === 'mine' && accessKeys.length === 0 ? (
            <div className="empty-state">
              <strong>No products configured</strong>
              Switch to "All Products" to add data access.
            </div>
          ) : (
            (viewMode === 'all' ? allProducts.map(p => p.product) : accessKeys).map(key => {
              const productDef  = allProducts.find(p => p.product === key);
              const granted     = !!access[key];
              const activityItems = (productDef?.activities ?? []).map(a => ({ value: a.activity, label: a.activity }));
              const regionItems   = [...new Set((productDef?.activities ?? []).flatMap(a => a.regions))].map(r => ({ value: r, label: r }));
              return (
                <div key={key} className={`product-card${granted ? '' : ' product-card--ungrated'}${animatingOut.has(key) ? ' product-card--removing' : ''}`}>
                  <div className="product-card-header">
                    <div className="product-card-title">
                      <span className={`product-tag ${getProductTagClass(key)}`}>{key}</span>
                      Data Access
                    </div>
                    <label className="granted-toggle">
                      <input
                        type="checkbox"
                        checked={granted}
                        onChange={e => e.target.checked ? grantProduct(key) : removeProduct(key)}
                      />
                      Granted
                    </label>
                  </div>
                  {granted && (
                    <div className="product-card-body">
                      <div className="access-section">
                        <h5>Activity</h5>
                        <CheckGroup
                          items={activityItems}
                          selected={access[key].activities}
                          onChange={next => setProductActivities(key, next)}
                        />
                      </div>
                      <div className="access-section">
                        <h5>Region</h5>
                        <CheckGroup
                          items={regionItems}
                          selected={access[key].regions}
                          onChange={next => setProductRegions(key, next)}
                          columns={2}
                        />
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>

        {accessKeys.length > 0 && (
          <p className="access-note">
            ℹ️ Changes to data access take effect immediately upon saving. Users with active sessions may need to refresh.
          </p>
        )}

        <div className="btn-row">
          <button className="btn btn-primary" onClick={handleSubmit} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
          <button className="btn btn-secondary" onClick={onCancel}>Cancel</button>
        </div>

        <ConfirmModal
          open={showGrantAllModal}
          onClose={() => setShowGrantAllModal(false)}
          onConfirm={grantAll}
          title="Grant All Permissions"
          message="This will grant access to ALL products with all activities and all regions. Are you sure?"
          confirmLabel="Grant All"
          cancelLabel="Cancel"
          confirmClass="button-danger"
          cancelClass="button-secondary"
        />
      </div>

      {/* ── Tab: Outlooks ── */}
      <div className={`admin-panel${activeTab === 'outlooks' ? ' active' : ''}`}>
        <div className="outlooks-grid">
          <div className="outlooks-group">
            <div className="outlooks-group-header">
              🌐 Global Outlooks
            </div>
            <div className="outlooks-group-body">
              <CheckGroup
                items={globalOutlookItems}
                selected={outlooks.global}
                onChange={next => setOutlookGroup('global', next)}
              />
            </div>
          </div>
          <div className="outlooks-group">
            <div className="outlooks-group-header">
              🗺️ Regional Watches
            </div>
            <div className="outlooks-group-body">
              <CheckGroup
                items={regionalWatchItems}
                selected={outlooks.regional}
                onChange={next => setOutlookGroup('regional', next)}
              />
            </div>
          </div>
        </div>

        <div className="btn-row">
          <button className="btn btn-primary" onClick={handleSubmit} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
          <button className="btn btn-secondary" onClick={onCancel}>Cancel</button>
        </div>
      </div>

      {/* ── Tab: Publications ── */}
      <div className={`admin-panel${activeTab === 'publications' ? ' active' : ''}`}>
        <div className="outlooks-grid">
          <div className="outlooks-group" style={{ gridColumn: '1 / -1' }}>
            <div className="outlooks-group-header">
              Publication Access
            </div>
            <div className="outlooks-group-body">
              <CheckGroup
                items={pubTagItems}
                selected={pubAccess}
                onChange={next => setPubAccess(next)}
              />
            </div>
          </div>
        </div>

        <div className="btn-row">
          <button className="btn btn-primary" onClick={handleSubmit} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
          <button className="btn btn-secondary" onClick={onCancel}>Cancel</button>
        </div>
      </div>

      {/* ── Tab: Users ── */}
      <div className={`admin-panel${activeTab === 'users' ? ' active' : ''}`}>
        {customerState.id
          ? <UserList customerId={customerState.id} />
          : <div className="empty-state"><strong>Save the customer first</strong>Users can be added after the customer record is created.</div>
        }
      </div>
    </div>
  );
}
